/**
 * State store for the wallet (saved payment methods). Reads the current user's saved cards reactively
 * from Firestore (safe metadata only) and exposes the wallet mutations. Card capture itself (the PCI
 * Booking hosted form + postMessage handshake) lives in the platform capture surfaces — the web
 * `CardCaptureFrame` iframe and the mobile `CardCaptureWebView` — this store just mints the session and
 * persists the resulting token via the server flow.
 *
 * Shared by web and mobile (and, in due course, the mobile checkout card selector): it depends only on
 * `SessionState` + the wallet container, both platform-neutral, so it lives here in `client-core`.
 */

import type { PaymentMethodsContainer } from '#containers/PaymentMethodsContainer.js';
import type { PaymentMethod } from '#models/PaymentMethodModel.js';
import type { SessionState } from './SessionState.js';

import { trackAddPaymentInfo } from '#lib/analytics-events.js';
import { createState } from '#lib/signals-utils.js';

type AddCardInput = Parameters<PaymentMethodsContainer['ClientPaymentMethod']['addPaymentMethod']>[0];

export function createPaymentMethodsState(sessionState: SessionState, wallet: PaymentMethodsContainer) {
  const model = wallet.ClientPaymentMethod;

  const state = createState({
    /** Reactive Firestore query of the current user's saved cards (undefined when logged out). */
    get query() {
      const userId = sessionState.userId;
      if (!userId) return undefined;
      return model.list({ userId });
    },

    /** Whether the saved-cards query has loaded. */
    get isReady(): boolean {
      const query = this.query;
      return Boolean(query?.isReady);
    },

    /** The current user's saved cards (empty until ready). */
    get cards(): PaymentMethod[] {
      const query = this.query;
      if (!query || !query.isReady) return [];
      return query.items as PaymentMethod[];
    },

    /** The default card, falling back to the most recent if none is flagged. */
    get defaultCard(): PaymentMethod | undefined {
      return this.cards.find((card) => card.isDefault) ?? this.cards[0];
    },

    /** Mint a PCI Booking capture session for the given page origin; returns the iframe URL to embed. */
    async mintCaptureSession(clientOrigin: string): Promise<string> {
      const result = await model.createCardCaptureSession({ clientOrigin });
      return result.data.iframeUrl;
    },

    /** Persist a freshly captured card (token + safe metadata). Returns the new card's id. */
    async addCard(input: AddCardInput): Promise<string> {
      const result = await model.addPaymentMethod(input);
      trackAddPaymentInfo();
      return result.data.id;
    },

    /** Permanently remove a saved card. */
    async removeCard(id: string): Promise<void> {
      await model.deletePaymentMethod({ id });
    },

    /** Make a card the default (unsets any prior default server-side). */
    async setDefault(id: string): Promise<void> {
      await model.updatePaymentMethod({ id, isDefault: true });
    },

    /** Rename a saved card. */
    async rename(id: string, nickname: string): Promise<void> {
      await model.updatePaymentMethod({ id, nickname });
    },
  });

  return state;
}

export type PaymentMethodsState = ReturnType<typeof createPaymentMethodsState>;
